import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  Search,
  Filter,
  Upload,
  Download,
  UserPlus,
  MessageSquare,
  MoreVertical,
  Copy,
  Phone,
  X,
  Users,
  Edit,
  Trash2,
  Mic,
  Video,
  ImageIcon,
  FileText,
  Paperclip,
} from 'lucide-react';
import api, {
  formatBrazilianPhone,
  formatBrazilianPhoneInput,
  parseApiError,
  validateBrazilianWhatsAppPhone,
  type ParsedApiError,
} from '../services/api.ts';
import { formatLastMessage } from '../utils/lastMessagePreview.ts';
import ContactAvatar from '../components/ContactAvatar.tsx';
import ContactsImport from '../components/ContactsImport.tsx';
import WhatsAppIdentityErrorAlert from '../components/WhatsAppIdentityErrorAlert.tsx';
import * as XLSX from 'xlsx';
import TagSelector from '../components/TagSelector.tsx';
import { TagsService } from '../services/apiTags.ts';

// Utility functions
const cx = (...args: (string | false | null | undefined)[]) => args.filter(Boolean).join(" ");

// Parse search query for tokenized filters
type Query = { text?: string; status?: string; tag?: string; owner?: string };
function parseQuery(q: string): Query {
  const parts = q.trim().split(/\s+/).filter(Boolean);
  const res: Query = {};
  const rest: string[] = [];
  for (const p of parts) {
    if (p.startsWith("status:")) res.status = p.slice(7);
    else if (p.startsWith("tag:")) res.tag = p.slice(4);
    else if (p.startsWith("dono:") || p.startsWith("owner:")) res.owner = p.split(":")[1];
    else rest.push(p);
  }
  if (rest.length) res.text = rest.join(" ");
  return res;
}

// Status colors following company theme and chat pattern
const getStatusColors = (status: string, isDark: boolean) => {
  const statusColors = {
    // Contato é cinza claro
    contato: isDark
      ? 'bg-gray-700 text-gray-300'
      : 'bg-gray-100 text-gray-700',
    // Lead é azul claro
    lead: isDark
      ? 'bg-blue-900/50 text-blue-300'
      : 'bg-blue-100 text-blue-700',
    // Agendado é amarelo
    agendado: isDark
      ? 'bg-yellow-900/50 text-yellow-300'
      : 'bg-yellow-100 text-yellow-700',
    // Paciente é roxo/purple
    paciente: isDark
      ? 'bg-purple-900/50 text-purple-300'
      : 'bg-purple-100 text-purple-700',
    // Compareceu é verde
    compareceu: isDark
      ? 'bg-green-900/50 text-green-300'
      : 'bg-green-100 text-green-700',
    // Faltou é vermelho
    faltou: isDark
      ? 'bg-red-900/50 text-red-300'
      : 'bg-red-100 text-red-700',
    // Venda usa cor da empresa mais vibrante
    venda: isDark
      ? 'bg-emerald-900/50 text-emerald-300'
      : 'bg-emerald-100 text-emerald-700',
    // Default
    default: isDark
      ? 'bg-gray-700 text-gray-300'
      : 'bg-gray-100 text-gray-700'
  };

  return statusColors[status as keyof typeof statusColors] || statusColors.default;
};

interface Contact {
  id: number;
  phone: string;
  name: string;
  photo?: string;
  unread_count: number;
  last_message_at?: string;
  last_message?: string;
  last_message_type?: string | null;
  human_mode: boolean;
  funnel_stage?: string;
  active_flows?: string[];
  lead_id?: number;
  paciente_id?: number;
  archived?: boolean;
  tags?: { id: number; name: string; color: string; category: string }[];
}

interface ContactsResponse {
  contacts: Contact[];
  total: number;
  has_more: boolean;
}



const ContactsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [includeArchivedSearch, setIncludeArchivedSearch] = useState(false);
  const [isDuplicateReview, setIsDuplicateReview] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [contactToConvert, setContactToConvert] = useState<Contact | null>(null);
  const [leadConversionError, setLeadConversionError] = useState<ParsedApiError | null>(null);
  const [newContactError, setNewContactError] = useState<ParsedApiError | null>(null);
  const [selectedSource, setSelectedSource] = useState('Facebook');
  const [editName, setEditName] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const [showFilters, setShowFilters] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [filterTags, setFilterTags] = useState<number[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const limit = 50;

  const tokens = useMemo(() => parseQuery(query), [query]);

  useEffect(() => {
    const state = location.state as {
      searchTerm?: string;
      includeArchived?: boolean;
      existingContactId?: number;
      reviewDuplicates?: boolean;
    } | null;
    if (state?.searchTerm) {
      setQuery(state.searchTerm);
      setIncludeArchivedSearch(
        Boolean(state.includeArchived || state.existingContactId),
      );
      setIsDuplicateReview(
        Boolean(
          state.reviewDuplicates ||
            state.includeArchived ||
            state.existingContactId,
        ),
      );
      setSelected(new Set());
      setCurrentPage(1);
    }
  }, [location.key, location.state]);

  const fetchContacts = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const clinicId = localStorage.getItem('clinic_id');
      if (!clinicId) {
        setError('ID da clínica não encontrado');
        return;
      }

      const offset = (currentPage - 1) * limit;
      const params = new URLSearchParams({
        clinic_id: clinicId,
        limit: limit.toString(),
        offset: offset.toString(),
        unread_only: unreadOnly.toString(),
      });

      // Lógica correta para arquivados
      if (showArchived) {
        params.append('archived_only', 'true');
      } else if (includeArchivedSearch && tokens.text?.trim()) {
        params.append('show_archived', 'true');
      } else {
        params.append('show_archived', 'false');
      }

      if (tokens.text?.trim()) {
        params.append('search', tokens.text.trim());
      }

      if (tokens.status?.trim()) {
        params.append('funnel_stages', tokens.status.trim());
      }

      // Filtro por tags
      if (filterTags.length > 0) {
        params.append('tag_ids', filterTags.join(','));
      }

      const response = await api.get<ContactsResponse>(`/webhook/contacts?${params}`);

      setContacts(response.data.contacts);
      setTotal(response.data.total);
    } catch (err: any) {
      const errorMessage = typeof err === 'string' ? err :
        err.response?.data?.detail ||
        err.message ||
        'Erro ao carregar contatos';
      setError(errorMessage);
      console.error('Erro ao buscar contatos:', err);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    limit,
    unreadOnly,
    showArchived,
    includeArchivedSearch,
    tokens,
    filterTags,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      // Se mudar a query, resetamos a página para 1. 
      // Se a página já for 1, o fetchContacts já foi disparado pela mudança de tokens (que está nas deps de fetchContacts)
      // Então aqui só precisamos garantir que se a query mudou e a página não é 1, voltamos para 1.
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [query, currentPage]);

  useEffect(() => {
    setSelected(new Set());
    setShowBulkDeleteModal(false);
  }, [
    query,
    showArchived,
    includeArchivedSearch,
    unreadOnly,
    filterTags,
  ]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Fechar filtros se clicar fora
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(target)) {
        setShowFilters(false);
      }

      // Fechar dropdown de ações apenas se clicar fora do dropdown
      if (dropdownOpen !== null) {
        const dropdownElement = target.closest('[data-dropdown]');
        if (!dropdownElement) {
          setDropdownOpen(null);
        }
      }
    };

    if (showFilters || dropdownOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilters, dropdownOpen]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 dias
      return date.toLocaleDateString('pt-BR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };


  const totalPages = Math.ceil(total / limit);

  // Selection functions
  const allVisibleSelected = contacts.length > 0 && contacts.every(c => c.id && selected.has(c.id.toString()));

  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        contacts.forEach(c => {
          if (c.id) next.delete(c.id.toString());
        });
      } else {
        contacts.forEach(c => {
          if (c.id) next.add(c.id.toString());
        });
      }
      return next;
    });
  };

  // Selecionar TODOS os contatos do filtro (busca todos os IDs com paginação)
  const selectAllFiltered = async () => {
    try {
      setSelectAllLoading(true);

      const clinicId = localStorage.getItem('clinic_id');
      if (!clinicId) return;

      const allIds: string[] = [];
      let offset = 0;
      const batchLimit = 500; // Máximo permitido pelo backend
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          clinic_id: clinicId,
          limit: batchLimit.toString(),
          offset: offset.toString(),
          unread_only: unreadOnly.toString(),
        });

        if (showArchived) {
          params.append('archived_only', 'true');
        } else if (includeArchivedSearch && tokens.text?.trim()) {
          params.append('show_archived', 'true');
        } else {
          params.append('show_archived', 'false');
        }

        if (tokens.text?.trim()) {
          params.append('search', tokens.text.trim());
        }

        if (tokens.status?.trim()) {
          params.append('funnel_stages', tokens.status.trim());
        }

        if (filterTags.length > 0) {
          params.append('tag_ids', filterTags.join(','));
        }

        const response = await api.get<ContactsResponse>(`/webhook/contacts?${params}`);
        const batchIds = response.data.contacts.map(c => c.id.toString());
        allIds.push(...batchIds);

        hasMore = response.data.has_more && batchIds.length === batchLimit;
        offset += batchLimit;

        if (offset > 50000) break; // Proteção contra loop infinito
      }

      console.log('[selectAllFiltered] Total selecionados:', allIds.length);
      setSelected(new Set(allIds));
    } catch (err) {
      console.error('Erro ao selecionar todos:', err);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const clearSelection = () => {
    setSelected(new Set());
  };

  // Remove token from search
  const removeToken = (key: keyof Query) => {
    const parts = query.split(/\s+/).filter(Boolean).filter(p => {
      if (key === "status" && p.startsWith("status:")) return false;
      if (key === "tag" && p.startsWith("tag:")) return false;
      if (key === "owner" && (p.startsWith("dono:") || p.startsWith("owner:"))) return false;
      return true;
    });
    setQuery(parts.join(" "));
  };

  // Output padding
  const rowPadding = density === "compact" ? "py-2" : "py-3";

  // Apply status filter
  const applyStatusFilter = (status: string) => {
    // Remove existing status filter first
    const cleanQuery = query.split(/\s+/).filter(p => !p.startsWith("status:")).join(" ");
    const newQuery = cleanQuery.trim() ? `${cleanQuery} status:${status}` : `status:${status}`;
    setQuery(newQuery);
    setShowFilters(false);
  };

  // Available status options
  const statusOptions = [
    { value: 'contato', label: 'Contato', color: 'bg-gray-100 text-gray-700' },
    { value: 'lead', label: 'Lead', color: 'bg-blue-100 text-blue-700' },
    { value: 'agendado', label: 'Agendado', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'paciente', label: 'Paciente', color: 'bg-purple-100 text-purple-700' },
    { value: 'compareceu', label: 'Compareceu', color: 'bg-green-100 text-green-700' },
    { value: 'faltou', label: 'Faltou', color: 'bg-red-100 text-red-700' },
    { value: 'venda', label: 'Venda', color: 'bg-emerald-100 text-emerald-700' },
  ];

  const handleConvertToLead = async (contact: Contact) => {
    setContactToConvert(contact);
    setSelectedSource('Facebook');
    setLeadConversionError(null);
    setShowLeadModal(true);
  };

  const confirmConvertToLead = async () => {
    if (!contactToConvert || actionLoading === contactToConvert.id) return;

    const phoneValidation = validateBrazilianWhatsAppPhone(contactToConvert.phone);
    if (!phoneValidation.isValid) {
      setLeadConversionError({
        message: phoneValidation.error || 'Telefone inválido.',
        code: 'invalid_phone',
      });
      return;
    }

    try {
      setLeadConversionError(null);
      setActionLoading(contactToConvert.id);
      await api.post(`/webhook/contacts/${contactToConvert.id}/convert-to-lead`, {
        source_id: selectedSource
      });
      await fetchContacts(); // Recarregar lista
      setShowLeadModal(false);
      setContactToConvert(null);
    } catch (err: unknown) {
      setLeadConversionError(parseApiError(err, 'Erro ao converter para lead'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleConvertToPatient = async (contact: Contact) => {
    setContactToConvert(contact);
    setShowPatientModal(true);
  };

  const confirmConvertToPatient = async () => {
    if (!contactToConvert) return;

    try {
      setActionLoading(contactToConvert.id);
      await api.post(`/webhook/contacts/${contactToConvert.id}/convert-to-patient`);
      await fetchContacts(); // Recarregar lista
      setShowPatientModal(false);
      setContactToConvert(null);
    } catch (err: any) {
      const errorMessage = typeof err === 'string' ? err :
        err.response?.data?.detail ||
        err.message ||
        'Erro ao converter para paciente';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleImportComplete = () => {
    fetchContacts(); // Recarregar contatos após importação
    setShowImport(false);
  };

  const handleExportContacts = async () => {
    try {
      setLoading(true);
      setError(null); // Limpar erros anteriores

      const clinicId = localStorage.getItem('clinic_id');
      if (!clinicId) {
        setError('ID da clínica não encontrado');
        return;
      }

      // Buscar todos os contatos com paginação (limite máximo é 500)
      let allContacts: Contact[] = [];
      let offset = 0;
      const limit = 500; // Limite máximo permitido pelo backend
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          clinic_id: clinicId,
          limit: limit.toString(),
          offset: offset.toString(),
          unread_only: unreadOnly.toString(),
        });

        // Aplicar os mesmos filtros da tela
        if (showArchived) {
          params.append('archived_only', 'true');
        } else if (includeArchivedSearch && tokens.text?.trim()) {
          params.append('show_archived', 'true');
        } else {
          params.append('show_archived', 'false');
        }

        if (tokens.text?.trim()) {
          params.append('search', tokens.text.trim());
        }

        if (tokens.status?.trim()) {
          params.append('funnel_stages', tokens.status.trim());
        }

        const response = await api.get<ContactsResponse>(`/webhook/contacts?${params}`);
        const pageContacts = response.data.contacts || [];

        allContacts = [...allContacts, ...pageContacts];

        // Verificar se há mais páginas
        hasMore = response.data.has_more && pageContacts.length === limit;
        offset += limit;

        // Proteção contra loop infinito
        if (offset > 10000) {
          console.warn('Limite de 10.000 contatos atingido para exportação');
          break;
        }
      }

      if (allContacts.length === 0) {
        setError('Nenhum contato encontrado para exportar');
        return;
      }

      // Preparar dados para o Excel
      const exportData = allContacts.map(contact => ({
        'Nome': String(contact.name || 'Sem nome'),
        'Telefone': String(contact.phone || ''),
        'Cadastro arquivado': contact.archived ? 'Sim' : 'Não',
        'Status': String(contact.funnel_stage || 'contato'),
        'Mensagens não lidas': Number(contact.unread_count || 0),
        'Última mensagem': String(contact.last_message || '-').substring(0, 100), // Limitar tamanho
        'Data última interação': contact.last_message_at ?
          new Date(contact.last_message_at).toLocaleString('pt-BR') : '-',
        'Modo humano': contact.human_mode ? 'Sim' : 'Não',
        'É Lead': contact.lead_id ? 'Sim' : 'Não',
        'É Paciente': contact.paciente_id ? 'Sim' : 'Não'
      }));

      // Criar workbook e worksheet
      try {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Contatos');

        // Ajustar largura das colunas
        const colWidths = [
          { wch: 25 }, // Nome
          { wch: 15 }, // Telefone
          { wch: 18 }, // Cadastro arquivado
          { wch: 12 }, // Status
          { wch: 8 },  // Não lidas
          { wch: 30 }, // Última mensagem
          { wch: 20 }, // Data
          { wch: 12 }, // Modo humano
          { wch: 8 },  // É Lead
          { wch: 12 }  // É Paciente
        ];
        ws['!cols'] = colWidths;

        // Gerar nome do arquivo com data
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const filename = `contatos_${dateStr}.xlsx`;

        // Download do arquivo
        XLSX.writeFile(wb, filename);

        console.log(`Exportação concluída: ${exportData.length} contatos exportados`);

      } catch (xlsxError) {
        console.error('Erro ao criar arquivo Excel:', xlsxError);
        throw new Error('Erro ao gerar arquivo Excel');
      }

    } catch (err: any) {
      const errorMessage = typeof err === 'string' ? err :
        err.response?.data?.detail ||
        err.message ||
        'Erro ao exportar contatos';
      setError(errorMessage);
      console.error('Erro ao exportar contatos:', err);
    } finally {
      setLoading(false);
    }
  };

  const [editTags, setEditTags] = useState<number[]>([]);

  const handleEditContact = (contact: Contact) => {
    setContactToConvert(contact);
    setEditName(contact.name || '');
    setEditTags(contact.tags?.map(t => t.id) || []);
    setShowEditModal(true);
  };

  const confirmEditContact = async () => {
    if (!contactToConvert || !editName.trim()) return;

    try {
      setActionLoading(contactToConvert.id);

      // Atualizar nome
      await api.put(`/webhook/contacts/${contactToConvert.id}/edit`, {
        name: editName.trim()
      });

      // Atualizar tags (usando batch replace para garantir consistência)
      if (editTags.length !== (contactToConvert.tags?.length || 0) ||
        !editTags.every(t => contactToConvert.tags?.some(ct => ct.id === t))) {

        await TagsService.batchOperationContacts({
          contact_ids: [contactToConvert.id],
          tag_ids: editTags,
          operation: 'replace'
        });
      }

      await fetchContacts(); // Recarregar lista
      setShowEditModal(false);
      setContactToConvert(null);
      setEditName('');
      setEditTags([]);
    } catch (err: any) {
      const errorMessage = typeof err === 'string' ? err :
        err.response?.data?.detail ||
        err.message ||
        'Erro ao editar contato';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteContact = (contact: Contact) => {
    setContactToConvert(contact);
    setShowDeleteModal(true);
  };

  const confirmDeleteContact = async () => {
    if (!contactToConvert) return;

    try {
      setActionLoading(contactToConvert.id);
      await api.delete(`/webhook/contacts/${contactToConvert.id}/delete`);
      await fetchContacts(); // Recarregar lista
      setShowDeleteModal(false);
      setContactToConvert(null);
    } catch (err: any) {
      const errorMessage = typeof err === 'string' ? err :
        err.response?.data?.detail ||
        err.message ||
        'Erro ao excluir contato';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyPhone = async (phone: string) => {
    try {
      await navigator.clipboard.writeText(phone);
      // Você pode adicionar uma notificação de sucesso aqui se quiser
    } catch (err) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = phone;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBrazilianPhoneInput(e.target.value);
    setNewContactError(null);
    setNewContactPhone(formatted);
  };

  const handleCreateNewContact = async () => {
    if (actionLoading === -1) return;

    if (!newContactName.trim() || !newContactPhone.trim()) {
      setNewContactError({ message: 'Nome e telefone são obrigatórios.' });
      return;
    }

    const phoneValidation = validateBrazilianWhatsAppPhone(newContactPhone);
    if (!phoneValidation.isValid) {
      setNewContactError({
        message: phoneValidation.error || 'Telefone inválido.',
        code: 'invalid_phone',
      });
      return;
    }

    try {
      setNewContactError(null);
      setActionLoading(-1); // -1 indica loading do novo contato

      const clinicId = localStorage.getItem('clinic_id');
      if (!clinicId) {
        setNewContactError({ message: 'ID da clínica não encontrado.' });
        return;
      }

      await api.post('/webhook/contacts/create', {
        name: newContactName.trim(),
        phone: phoneValidation.normalized,
        clinic_id: parseInt(clinicId)
      });

      await fetchContacts(); // Recarregar lista
      setShowNewContactModal(false);
      setNewContactName('');
      setNewContactPhone('');
      setNewContactError(null);
    } catch (err: unknown) {
      setNewContactError(parseApiError(err, 'Erro ao criar contato'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenExistingIdentity = (
    identityError: ParsedApiError,
    fallbackPhone: string,
  ) => {
    const phone = identityError.existingPhone || fallbackPhone;

    if (identityError.existingLeadId) {
      setShowLeadModal(false);
      setShowNewContactModal(false);
      setContactToConvert(null);
      setLeadConversionError(null);
      setNewContactError(null);
      navigate('/crm', {
        state: {
          searchTerm: phone,
          existingLeadId: identityError.existingLeadId,
        },
      });
      return;
    }

    setQuery(phone);
    setIncludeArchivedSearch(Boolean(identityError.existingContactId));
    setIsDuplicateReview(Boolean(identityError.existingContactId));
    setSelected(new Set());
    setCurrentPage(1);
    setShowLeadModal(false);
    setShowNewContactModal(false);
    setContactToConvert(null);
    setLeadConversionError(null);
    setNewContactError(null);
  };

  const handleOpenChat = (contact: Contact) => {
    navigate('/chat', {
      state: {
        selectedPhone: contact.phone,
        selectedContact: {
          id: contact.id,
          name: contact.name || 'Contato sem nome',
          phone: contact.phone,
          photo: contact.photo,
        }
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (isDuplicateReview) {
      setError(
        'Na revisão de duplicidades, exclua um cadastro por vez após confirmar se ele está ativo ou arquivado.',
      );
      setShowBulkDeleteModal(false);
      return;
    }

    try {
      setBulkDeleteLoading(true);
      setError(null);

      const contactIds = Array.from(selected).map(Number);
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Excluir cada contato individualmente
      for (const contactId of contactIds) {
        try {
          await api.delete(`/webhook/contacts/${contactId}/delete`);
          successCount++;
        } catch (err: any) {
          errorCount++;
          const contactName = contacts.find(c => c.id === contactId)?.name || contactId;
          errors.push(`${contactName}: ${err.response?.data?.detail || 'Erro desconhecido'}`);
        }
      }

      // Limpar seleção e recarregar
      setSelected(new Set());
      setShowBulkDeleteModal(false);
      await fetchContacts();

      // Mostrar resultado
      if (errorCount > 0) {
        setError(`Excluídos: ${successCount}. Erros: ${errorCount}. ${errors.slice(0, 3).join('; ')}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao excluir contatos em massa');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  return (
    <div className={`min-h-screen w-full ${isDark
      ? 'bg-gradient-to-b from-slate-950 to-slate-900 text-slate-100'
      : 'bg-gradient-to-b from-slate-50 to-white text-slate-800'
      }`}>
      <div className="mx-auto max-w-7xl p-4">
        <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-800'
              }`}>Lista de contatos</h1>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'
              }`}>Gerencie contatos, leads e pacientes em um só lugar.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImport(!showImport)}
              className={`rounded-xl border px-3 py-2 text-sm transition-colors ${isDark
                ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
            >
              <Upload className="mr-1 inline h-4 w-4" />Importar
            </button>
            <button
              onClick={handleExportContacts}
              disabled={loading}
              className={`rounded-xl border px-3 py-2 text-sm transition-colors disabled:opacity-50 ${isDark
                ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
            >
              <Download className="mr-1 inline h-4 w-4" />
              {loading ? 'Exportando...' : 'Exportar'}
            </button>
            <button
              onClick={() => {
                setShowNewContactModal(true);
                setNewContactError(null);
              }}
              className="rounded-xl bg-sminy-primary px-3 py-2 text-sm text-white hover:bg-sminy-primary/90 transition-colors"
            >
              <UserPlus className="mr-1 inline h-4 w-4" />Novo contato
            </button>
          </div>
        </header>

        {showImport && (
          <div className="mb-4">
            <ContactsImport
              onImportComplete={handleImportComplete}
              onClose={() => setShowImport(false)}
            />
          </div>
        )}

        {/* Toolbar / Filtros */}
        <div className={`rounded-2xl border p-3 shadow-xl ${isDark
          ? 'border-slate-800 bg-slate-900'
          : 'border-slate-200 bg-white'
          }`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className={`flex w-full max-w-xl items-center gap-2 rounded-xl border px-3 py-2 ${isDark
                ? 'border-slate-700 bg-slate-800'
                : 'border-slate-200 bg-white'
                }`}>
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setIncludeArchivedSearch(false);
                  }}
                  className={`w-full bg-transparent text-sm outline-none ${isDark ? 'placeholder:text-slate-500 text-slate-200' : 'placeholder:text-slate-400 text-slate-800'
                    }`}
                  placeholder="Buscar por nome/telefone ou use filtros: status:lead tag:humano dono:elisa"
                />
              </div>
              <div className="relative" ref={filterDropdownRef}>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`rounded-xl border px-3 py-2 text-sm transition-colors ${isDark
                    ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  <Filter className="mr-1 inline h-4 w-4" />Filtros
                </button>

                {/* Dropdown de filtros */}
                {showFilters && (
                  <div className={`absolute top-full left-0 mt-1 z-10 min-w-[200px] rounded-xl border shadow-lg ${isDark
                    ? 'border-slate-700 bg-slate-800'
                    : 'border-slate-200 bg-white'
                    }`}>
                    <div className="p-3">
                      <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-slate-700'
                        }`}>Status</h3>
                      <div className="space-y-1">
                        {statusOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => applyStatusFilter(option.value)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${isDark
                              ? 'hover:bg-slate-700 text-slate-300'
                              : 'hover:bg-slate-50 text-slate-700'
                              }`}
                          >
                            <span className={`rounded-full px-2 py-0.5 text-xs ${isDark ? getStatusColors(option.value, true) : option.color
                              }`}>
                              {option.label}
                            </span>
                          </button>
                        ))}
                        {tokens.status && (
                          <>
                            <hr className={`my-2 ${isDark ? 'border-slate-700' : 'border-slate-200'
                              }`} />
                            <button
                              onClick={() => removeToken('status')}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${isDark
                                ? 'hover:bg-slate-700 text-slate-400'
                                : 'hover:bg-slate-50 text-slate-500'
                                }`}
                            >
                              Limpar filtro de status
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-48">
                <TagSelector
                  selectedTags={filterTags}
                  onTagsChange={setFilterTags}
                  clinicId={Number(localStorage.getItem('clinic_id'))}
                  variant="compact"
                  placeholder="Filtrar por tags..."
                  maxTags={5}
                />
              </div>
              <button
                onClick={() => setUnreadOnly(!unreadOnly)}
                className={cx(
                  "rounded-full px-3 py-1 ring-1 transition-colors",
                  unreadOnly
                    ? isDark
                      ? "bg-sminy-primary/20 text-sminy-primary ring-sminy-primary/30"
                      : "bg-sminy-primary/10 text-sminy-primary ring-sminy-primary/20"
                    : isDark
                      ? "bg-slate-800 text-slate-300 ring-slate-600"
                      : "bg-white text-slate-600 ring-slate-200"
                )}
              >
                Não lidas
              </button>
              <button
                onClick={() => {
                  setShowArchived(!showArchived);
                  setIncludeArchivedSearch(false);
                  setIsDuplicateReview(false);
                  setSelected(new Set());
                }}
                className={cx(
                  "rounded-full px-3 py-1 ring-1 transition-colors",
                  showArchived
                    ? isDark
                      ? "bg-slate-700 text-slate-300 ring-slate-600"
                      : "bg-slate-100 text-slate-700 ring-slate-200"
                    : isDark
                      ? "bg-slate-800 text-slate-300 ring-slate-600"
                      : "bg-white text-slate-600 ring-slate-200"
                )}
              >
                {showArchived ? 'Só arquivados' : 'Incluir arquivados'}
              </button>
            </div>
          </div>

          {isDuplicateReview && includeArchivedSearch && tokens.text && (
            <div
              role="status"
              className={`mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm ${
                isDark
                  ? 'border-amber-800 bg-amber-950/30 text-amber-200'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              <span>
                Compare os selos <strong>Ativo</strong> e <strong>Arquivado</strong>{' '}
                antes de agir. Para evitar apagar o cadastro errado, a exclusão em
                massa fica indisponível nesta revisão.
              </span>
              <button
                type="button"
                onClick={() => {
                  setIncludeArchivedSearch(false);
                  setIsDuplicateReview(false);
                  setSelected(new Set());
                }}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isDark
                    ? 'bg-amber-900/50 hover:bg-amber-900'
                    : 'bg-amber-100 hover:bg-amber-200'
                }`}
              >
                Encerrar revisão
              </button>
            </div>
          )}

          {/* Chips de tokens ativos */}
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {tokens.status && (
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${isDark
                ? 'bg-slate-700 text-slate-300'
                : 'bg-slate-100 text-slate-700'
                }`}>
                status:{tokens.status}
                <button
                  onClick={() => removeToken("status")}
                  className={`ml-1 rounded p-0.5 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-200'
                    }`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {tokens.tag && (
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${isDark
                ? 'bg-slate-700 text-slate-300'
                : 'bg-slate-100 text-slate-700'
                }`}>
                tag:{tokens.tag}
                <button
                  onClick={() => removeToken("tag")}
                  className={`ml-1 rounded p-0.5 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-200'
                    }`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {tokens.owner && (
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${isDark
                ? 'bg-slate-700 text-slate-300'
                : 'bg-slate-100 text-slate-700'
                }`}>
                dono:{tokens.owner}
                <button
                  onClick={() => removeToken("owner")}
                  className={`ml-1 rounded p-0.5 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-200'
                    }`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>

          {/* Barra de ações em massa */}
          {selected.size > 0 && (
            <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${isDark
              ? 'border-slate-700 bg-slate-800 text-slate-300'
              : 'border-slate-200 bg-slate-50 text-slate-700'
              }`}>
              <div className="flex items-center gap-3">
                <p><strong>{selected.size}</strong> de <strong>{total}</strong> selecionado(s)</p>

                {/* Opção de selecionar todos os filtrados */}
                {selected.size < total && (
                  <button
                    onClick={selectAllFiltered}
                    disabled={selectAllLoading}
                    className={`text-xs px-2 py-1 rounded-lg transition-colors ${isDark
                      ? 'bg-sminy-primary/20 text-sminy-primary hover:bg-sminy-primary/30'
                      : 'bg-sminy-primary/10 text-sminy-primary hover:bg-sminy-primary/20'
                      }`}
                  >
                    {selectAllLoading ? 'Carregando...' : `Selecionar todos os ${total}`}
                  </button>
                )}

                {/* Limpar seleção */}
                <button
                  onClick={clearSelection}
                  className={`text-xs px-2 py-1 rounded-lg transition-colors ${isDark
                    ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                >
                  Limpar seleção
                </button>
              </div>

              <div className="flex items-center gap-1">
                <span className={`text-xs mr-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Ações:</span>
                <TagSelector
                  selectedTags={(() => {
                    if (selected.size === 0) return [];
                    const selectedContacts = contacts.filter(c => selected.has(c.id.toString()));
                    if (selectedContacts.length === 0) return [];

                    const firstContactTags = new Set(selectedContacts[0].tags?.map(t => t.id) || []);
                    let commonTagIds = Array.from(firstContactTags);

                    for (let i = 1; i < selectedContacts.length; i++) {
                      const contactTags = new Set(selectedContacts[i].tags?.map(t => t.id) || []);
                      commonTagIds = commonTagIds.filter(tagId => contactTags.has(tagId));
                    }
                    return commonTagIds;
                  })()}

                  onTagsChange={async (newTags) => {
                    if (selected.size === 0) return;
                    try {
                      // Recalcular as tags comuns para saber o que mudou
                      const selectedContacts = contacts.filter(c => selected.has(c.id.toString()));
                      const firstContactTags = new Set(selectedContacts[0].tags?.map(t => t.id) || []);
                      let commonTagIds = Array.from(firstContactTags);
                      for (let i = 1; i < selectedContacts.length; i++) {
                        const contactTags = new Set(selectedContacts[i].tags?.map(t => t.id) || []);
                        commonTagIds = commonTagIds.filter(tagId => contactTags.has(tagId));
                      }

                      const added = newTags.filter(id => !commonTagIds.includes(id));
                      const removed = commonTagIds.filter(id => !newTags.includes(id));

                      if (added.length === 0 && removed.length === 0) return;

                      const confirmMsg = added.length > 0 && removed.length > 0
                        ? `Deseja adicionar ${added.length} tag(s) e remover ${removed.length} tag(s) de ${selected.size} contatos?`
                        : added.length > 0
                          ? `Deseja adicionar ${added.length} tag(s) a ${selected.size} contatos?`
                          : `Deseja remover ${removed.length} tag(s) de ${selected.size} contatos?`;

                      if (!window.confirm(confirmMsg)) return;

                      if (added.length > 0) {
                        await TagsService.batchOperationContacts({
                          contact_ids: Array.from(selected).map(Number),
                          tag_ids: added,
                          operation: 'add'
                        });
                      }

                      if (removed.length > 0) {
                        await TagsService.batchOperationContacts({
                          contact_ids: Array.from(selected).map(Number),
                          tag_ids: removed,
                          operation: 'remove'
                        });
                      }

                      alert('Tags atualizadas com sucesso!');
                      fetchContacts();
                      setSelected(new Set());
                    } catch (err: any) {
                      alert('Erro ao atualizar tags em massa');
                      console.error(err);
                    }
                  }}
                  clinicId={Number(localStorage.getItem('clinic_id'))}
                  variant="compact"
                  placeholder="Gerenciar tags..."
                  maxTags={10}
                />
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  disabled={isDuplicateReview}
                  title={
                    isDuplicateReview
                      ? 'Exclua um cadastro por vez após confirmar se ele está ativo ou arquivado.'
                      : 'Excluir contatos selecionados'
                  }
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50 ${isDark
                    ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                    }`}
                >
                  <Trash2 className="h-4 w-4" />
                  {isDuplicateReview ? 'Exclusão em massa indisponível' : 'Excluir'}
                </button>
              </div>
            </div>
          )}

          {loading ? (

            <div className="flex justify-center items-center py-8" >
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sminy-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className={`mb-4 ${isDark ? 'text-red-400' : 'text-red-600'
                }`}>{typeof error === 'string' ? error : 'Erro ao carregar contatos'}</p>
              <button
                onClick={fetchContacts}
                className="rounded-xl bg-sminy-primary px-3 py-2 text-sm text-white hover:bg-sminy-primary/90 transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            /* Tabela */
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={`text-left ${isDark ? 'text-slate-400' : 'text-slate-500'
                    }`}>
                    <th className="w-8 px-2 py-2">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="w-48 px-2 py-2">Nome</th>
                    <th className="min-w-[150px] px-2 py-2">Telefone</th>
                    <th className="min-w-[260px] px-2 py-2">Última interação</th>
                    <th className="w-28 px-2 py-2">Status</th>
                    <th className="w-48 px-2 py-2">Tags</th>
                    <th className="w-48 px-2 py-2 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-slate-800' : 'divide-slate-100'
                  }`}>
                  {contacts.map((contact) => {
                    const contactId = contact.id?.toString() || `temp-${Math.random()}`;
                    const isSelected = contact.id ? selected.has(contact.id.toString()) : false;
                    const unread = contact.unread_count > 0;
                    return (
                      <tr key={contactId} className={cx(
                        "group transition-colors",
                        unread && (isDark ? "bg-sminy-primary/10" : "bg-indigo-50/40")
                      )}>
                        <td className={cx("px-2", rowPadding)}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (contact.id) {
                                setSelected(prev => {
                                  const n = new Set(prev);
                                  n.has(contact.id.toString()) ? n.delete(contact.id.toString()) : n.add(contact.id.toString());
                                  return n;
                                });
                              }
                            }}
                          />
                        </td>
                        <td className={cx("px-2 max-w-48", rowPadding)}>
                          <div className="flex items-center gap-2 min-w-0">
                            <ContactAvatar photo={contact.photo} name={contact.name} className="h-8 w-8 rounded-full" iconClassName="h-4 w-4" />
                            <div className="min-w-0 flex-1">
                              <div className={cx(
                                "truncate",
                                unread && "font-semibold",
                                isDark ? 'text-slate-200' : 'text-slate-800'
                              )}>
                                {contact.name || 'Sem nome'}
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                                {(isDuplicateReview || contact.archived) && (
                                  <span
                                    className={`rounded-full px-2 py-0.5 font-semibold ${
                                      contact.archived
                                        ? isDark
                                          ? 'bg-slate-700 text-slate-300'
                                          : 'bg-slate-200 text-slate-700'
                                        : isDark
                                          ? 'bg-emerald-950/60 text-emerald-300'
                                          : 'bg-emerald-100 text-emerald-700'
                                    }`}
                                  >
                                    {contact.archived ? 'Arquivado' : 'Ativo'}
                                  </span>
                                )}
                                {contact.human_mode && (
                                  <span className={`rounded-full px-2 py-0.5 ${isDark
                                    ? 'bg-purple-900/50 text-purple-300'
                                    : 'bg-purple-100 text-purple-700'
                                    }`}>Humano</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={cx("px-2", rowPadding)}>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span className={`font-mono text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'
                              }`}>{contact.phone}</span>
                            <button
                              className={`rounded p-1 transition-colors ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                                }`}
                              title="Copiar telefone"
                              onClick={() => handleCopyPhone(contact.phone)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className={`rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                                }`}
                              title={
                                isDuplicateReview && contact.archived
                                  ? 'Cadastro arquivado: não é possível abrir para envio durante a revisão.'
                                  : 'Abrir Chat'
                              }
                              disabled={isDuplicateReview && contact.archived}
                              onClick={() => handleOpenChat(contact)}
                            >
                              <MessageSquare className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className={cx("px-2", rowPadding)}>
                          <div className="min-w-0">
                            <div className={`truncate ${isDark ? 'text-slate-300' : 'text-slate-700'
                              }`}>
                              {(() => {
                                const preview = formatLastMessage(contact.last_message, contact.last_message_type);
                                const Icon = { audio: Mic, video: Video, image: ImageIcon, document: FileText, media: Paperclip, text: null }[preview.type];
                                return <span className="flex min-w-0 items-center gap-1">
                                  {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
                                  <span className="truncate">{preview.text || '-'}</span>
                                </span>;
                              })()}
                            </div>
                            <div className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'
                              }`}>
                              {formatDate(contact.last_message_at)}
                            </div>
                          </div>
                        </td>
                        <td className={cx("px-2", rowPadding)}>
                          {contact.funnel_stage && (
                            <span className={cx(
                              "rounded-full px-2 py-0.5 text-xs",
                              getStatusColors(contact.funnel_stage, isDark)
                            )}>
                              {contact.funnel_stage}
                            </span>
                          )}
                        </td>
                        <td className={cx("px-2", rowPadding)}>
                          <div className="flex flex-wrap gap-1">
                            {contact.tags && contact.tags.length > 0 ? (
                              contact.tags.map(tag => (
                                <span
                                  key={tag.id}
                                  className="rounded-full px-2 py-0.5 text-[10px] font-medium border"
                                  style={{
                                    backgroundColor: isDark ? `${tag.color}30` : `${tag.color}20`,
                                    borderColor: `${tag.color}40`,
                                    color: tag.color
                                  }}
                                >
                                  {tag.name}
                                </span>
                              ))
                            ) : (
                              <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>-</span>
                            )}
                          </div>
                        </td>
                        <td className={cx("px-2", rowPadding)}>
                          <div className="flex items-center justify-center relative">
                            <button
                              className={`rounded-lg p-1 transition-colors ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                                }`}
                              onClick={() => {
                                setDropdownOpen(dropdownOpen === contact.id ? null : contact.id);
                              }}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>

                            {/* Dropdown de ações */}
                            {dropdownOpen === contact.id && (
                              <div
                                data-dropdown
                                className={`absolute top-full right-0 mt-1 z-50 min-w-[150px] rounded-xl border shadow-lg ${isDark
                                  ? 'border-slate-700 bg-slate-800'
                                  : 'border-slate-200 bg-white'
                                  }`}
                                style={{ pointerEvents: 'auto' }}
                              >
                                <div className="p-2">
                                  {/* Mostrar opções baseado no status */}
                                  {!contact.lead_id && (
                                    <button
                                      onClick={() => {
                                        setDropdownOpen(null);
                                        handleConvertToLead(contact);
                                      }}
                                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${isDark
                                        ? 'hover:bg-slate-700 text-slate-300'
                                        : 'hover:bg-slate-50 text-slate-700'
                                        }`}
                                      disabled={actionLoading === contact.id}
                                    >
                                      <UserPlus className="h-4 w-4" />
                                      Lead
                                    </button>
                                  )}

                                  {!contact.paciente_id && (
                                    <button
                                      onClick={() => {
                                        setDropdownOpen(null);
                                        handleConvertToPatient(contact);
                                      }}
                                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${isDark
                                        ? 'hover:bg-slate-700 text-slate-300'
                                        : 'hover:bg-slate-50 text-slate-700'
                                        }`}
                                      disabled={actionLoading === contact.id}
                                    >
                                      <Users className="h-4 w-4" />
                                      Paciente
                                    </button>
                                  )}

                                  {/* Se já é lead e paciente */}
                                  {contact.lead_id && contact.paciente_id && (
                                    <div className={`px-3 py-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'
                                      }`}>
                                      Contato completo
                                    </div>
                                  )}

                                  <hr className={`my-2 ${isDark ? 'border-slate-700' : 'border-slate-200'
                                    }`} />

                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDropdownOpen(null);
                                      handleEditContact(contact);
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${isDark
                                      ? 'hover:bg-slate-700 text-slate-300'
                                      : 'hover:bg-slate-50 text-slate-700'
                                      }`}
                                  >
                                    <Edit className="h-4 w-4" />
                                    Editar
                                  </button>

                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDropdownOpen(null);
                                      handleDeleteContact(contact);
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${isDark
                                      ? 'hover:bg-red-700/50 text-red-300'
                                      : 'hover:bg-red-50 text-red-700'
                                      }`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    {isDuplicateReview
                                      ? `Excluir cadastro ${contact.archived ? 'arquivado' : 'ativo'}`
                                      : 'Excluir'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {contacts.length === 0 && !loading && (
                <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                  Nenhum contato encontrado
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-6 px-2">
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                    }`}>
                    Mostrando {contacts.length} de {total} contatos
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`rounded-xl border px-3 py-2 text-sm disabled:opacity-50 transition-colors ${isDark
                        ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                      Anterior
                    </button>
                    <span className={`px-3 py-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                      {currentPage} de {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`rounded-xl border px-3 py-2 text-sm disabled:opacity-50 transition-colors ${isDark
                        ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de seleção de mídia para lead */}
      {
        showLeadModal && contactToConvert && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden ${isDark ? 'bg-slate-800 text-white' : 'bg-white'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                      }`}>Lead</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                      }`}>Selecione a mídia de origem</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowLeadModal(false);
                      setContactToConvert(null);
                      setLeadConversionError(null);
                    }}
                    disabled={actionLoading === contactToConvert.id}
                    className={`p-2 rounded-xl transition-colors ${isDark
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                      }`}>
                      Contato: {contactToConvert.name}
                    </label>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'
                      }`}>
                      {formatBrazilianPhone(contactToConvert.phone)}
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                      }`}>
                      Mídia de Origem
                    </label>
                    <select
                      value={selectedSource}
                      onChange={(e) => setSelectedSource(e.target.value)}
                      className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    >
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Google">Google</option>
                      <option value="Indicação">Indicação</option>
                      <option value="Fachada">Fachada</option>
                      <option value="Site">Site</option>
                      <option value="Orgânico">Orgânico</option>
                      <option value="Convênio">Convênio</option>
                      <option value="Carteira de Pacientes">Carteira de Pacientes</option>
                      <option value="Radio">Radio</option>
                      <option value="TV">TV</option>
                    </select>
                  </div>

                  {leadConversionError && (
                    <WhatsAppIdentityErrorAlert
                      error={leadConversionError}
                      onOpenExisting={() =>
                        handleOpenExistingIdentity(
                          leadConversionError,
                          contactToConvert.phone,
                        )
                      }
                    />
                  )}

                  <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                    <button
                      onClick={() => {
                        setShowLeadModal(false);
                        setContactToConvert(null);
                        setLeadConversionError(null);
                      }}
                      disabled={actionLoading === contactToConvert.id}
                      className={`flex-1 px-4 py-3 rounded-xl border font-medium transition-colors ${isDark
                        ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmConvertToLead}
                      disabled={actionLoading === contactToConvert.id}
                      className="flex-1 px-4 py-3 rounded-xl bg-sminy-primary text-white font-medium hover:bg-sminy-primary/90 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === contactToConvert.id
                        ? 'Verificando WhatsApp…'
                        : 'Converter para Lead'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal de confirmação para paciente */}
      {
        showPatientModal && contactToConvert && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden ${isDark ? 'bg-slate-800 text-white' : 'bg-white'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                      }`}>Paciente</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                      }`}>Confirme a conversão do contato</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowPatientModal(false);
                      setContactToConvert(null);
                    }}
                    className={`p-2 rounded-xl transition-colors ${isDark
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border ${isDark
                    ? 'bg-slate-700/50 border-slate-600'
                    : 'bg-blue-50 border-blue-200'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-sminy-primary/20' : 'bg-sminy-primary/10'
                        }`}>
                        <Users className="h-5 w-5 text-sminy-primary" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'
                          }`}>
                          {contactToConvert.name}
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'
                          }`}>
                          {contactToConvert.phone}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'
                    }`}>
                    <p>Este contato será marcado como <strong>paciente</strong> da clínica.</p>
                    <p className="mt-1">Você poderá gerenciar seus dados e histórico.</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowPatientModal(false);
                        setContactToConvert(null);
                      }}
                      className={`flex-1 px-4 py-3 rounded-xl border font-medium transition-colors ${isDark
                        ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmConvertToPatient}
                      disabled={actionLoading === contactToConvert.id}
                      className="flex-1 px-4 py-3 rounded-xl bg-sminy-primary text-white font-medium hover:bg-sminy-primary/90 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === contactToConvert.id ? 'Convertendo...' : 'Confirmar Paciente'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal de edição de nome */}
      {
        showEditModal && contactToConvert && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden ${isDark ? 'bg-slate-800 text-white' : 'bg-white'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                      }`}>Editar</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                      }`}>Alterar nome do contato</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setContactToConvert(null);
                      setEditName('');
                    }}
                    className={`p-2 rounded-xl transition-colors ${isDark
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                      }`}>
                      Nome do Contato
                    </label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      placeholder="Digite o nome do contato"
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'
                      }`}>
                      {contactToConvert.phone}
                    </p>
                  </div>

                  <div className={`p-3 rounded-lg border ${isDark
                    ? 'bg-blue-900/20 border-blue-700/50 text-blue-300'
                    : 'bg-blue-50 border-blue-200 text-blue-700'
                    }`}>
                    <p className="text-xs">
                      <strong>Nota:</strong> O nome será atualizado em todos os registros relacionados
                      (leads, agendamentos, comparecimentos, vendas).
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                      Tags
                    </label>
                    <TagSelector
                      selectedTags={editTags}
                      onTagsChange={setEditTags}
                      clinicId={Number(localStorage.getItem('clinic_id'))}
                      placeholder="Selecione as tags..."
                      maxTags={10}
                      allowCreate={true}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        setContactToConvert(null);
                        setEditName('');
                      }}
                      className={`flex-1 px-4 py-3 rounded-xl border font-medium transition-colors ${isDark
                        ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmEditContact}
                      disabled={actionLoading === contactToConvert.id || !editName.trim()}
                      className="flex-1 px-4 py-3 rounded-xl bg-sminy-primary text-white font-medium hover:bg-sminy-primary/90 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === contactToConvert.id ? 'Salvando...' : 'Salvar Nome'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal de confirmação de exclusão */}
      {
        showDeleteModal && contactToConvert && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden ${isDark ? 'bg-slate-800 text-white' : 'bg-white'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                      }`}>
                        Excluir cadastro {contactToConvert.archived ? 'arquivado' : 'ativo'}
                      </h3>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                      }`}>Esta ação não pode ser desfeita</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setContactToConvert(null);
                    }}
                    className={`p-2 rounded-xl transition-colors ${isDark
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border ${isDark
                    ? 'bg-red-900/20 border-red-700/50'
                    : 'bg-red-50 border-red-200'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-red-700/50' : 'bg-red-100'
                        }`}>
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'
                          }`}>
                          {contactToConvert.name}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              contactToConvert.archived
                                ? isDark
                                  ? 'bg-slate-700 text-slate-300'
                                  : 'bg-slate-200 text-slate-700'
                                : isDark
                                  ? 'bg-emerald-950/60 text-emerald-300'
                                  : 'bg-emerald-100 text-emerald-700'
                            }`}
                          >
                            {contactToConvert.archived ? 'Arquivado' : 'Ativo'}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            ID {contactToConvert.id}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'
                          }`}>
                          {contactToConvert.phone}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'
                    }`}>
                    <p className="font-medium">⚠️ ATENÇÃO: Esta ação irá excluir:</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside text-xs">
                      <li>Contato e histórico de mensagens</li>
                      <li>Dados de lead (se existir)</li>
                      <li>Dados de paciente (se existir)</li>
                      <li>Agendamentos relacionados</li>
                      <li>Comparecimentos e vendas</li>
                      <li>Eventos de no-show</li>
                    </ul>
                    <p className="mt-2 font-medium">Todos os dados serão perdidos permanentemente.</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setContactToConvert(null);
                      }}
                      className={`flex-1 px-4 py-3 rounded-xl border font-medium transition-colors ${isDark
                        ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmDeleteContact}
                      disabled={actionLoading === contactToConvert.id}
                      className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === contactToConvert.id ? 'Excluindo...' : 'Confirmar Exclusão'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal de exclusão em massa */}
      {
        showBulkDeleteModal && selected.size > 0 && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden ${isDark ? 'bg-slate-800 text-white' : 'bg-white'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                      }`}>Excluir em massa</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                      }`}>Esta ação não pode ser desfeita</p>
                  </div>
                  <button
                    onClick={() => setShowBulkDeleteModal(false)}
                    disabled={bulkDeleteLoading}
                    className={`p-2 rounded-xl transition-colors ${isDark
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border ${isDark
                    ? 'bg-red-900/20 border-red-700/50'
                    : 'bg-red-50 border-red-200'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-red-700/50' : 'bg-red-100'
                        }`}>
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'
                          }`}>
                          {selected.size} contatos selecionados
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'
                          }`}>
                          Serão excluídos permanentemente
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'
                    }`}>
                    <p className="font-medium">⚠️ ATENÇÃO: Para cada contato será excluído:</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside text-xs">
                      <li>Contato e histórico de mensagens</li>
                      <li>Dados de lead e paciente (se existir)</li>
                      <li>Agendamentos, comparecimentos e vendas</li>
                      <li>Eventos de no-show e tarefas</li>
                    </ul>
                    <p className="mt-2 font-medium">Todos os dados serão perdidos permanentemente.</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      disabled={bulkDeleteLoading}
                      className={`flex-1 px-4 py-3 rounded-xl border font-medium transition-colors ${isDark
                        ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteLoading}
                      className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {bulkDeleteLoading ? 'Excluindo...' : `Excluir ${selected.size} contatos`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal de novo contato */}
      {
        showNewContactModal && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden ${isDark ? 'bg-slate-800 text-white' : 'bg-white'
              }`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
                      }`}>Novo Contato</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'
                      }`}>Adicionar novo contato à lista</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowNewContactModal(false);
                      setNewContactName('');
                      setNewContactPhone('');
                      setNewContactError(null);
                    }}
                    disabled={actionLoading === -1}
                    className={`p-2 rounded-xl transition-colors ${isDark
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                      }`}>
                      Nome do Contato *
                    </label>
                    <input
                      type="text"
                      value={newContactName}
                      onChange={(e) => {
                        setNewContactName(e.target.value);
                        setNewContactError(null);
                      }}
                      className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      placeholder="Digite o nome do contato"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'
                      }`}>
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      value={newContactPhone}
                      onChange={handlePhoneChange}
                      className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${isDark
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Ex: (11) 99999-9999"
                    />
                  </div>

                  {newContactError && (
                    <WhatsAppIdentityErrorAlert
                      error={newContactError}
                      onOpenExisting={() =>
                        handleOpenExistingIdentity(
                          newContactError,
                          validateBrazilianWhatsAppPhone(newContactPhone).normalized,
                        )
                      }
                    />
                  )}

                  {/* Info box quando não há erro */}
                  {!newContactError && (
                    <div className={`p-3 rounded-lg border ${isDark
                      ? 'bg-blue-900/20 border-blue-700/50 text-blue-300'
                      : 'bg-blue-50 border-blue-200 text-blue-700'
                      }`}>
                      <p className="text-xs">
                        <strong>Info:</strong> O contato será criado e estará disponível para
                        conversas no chat. Você poderá convertê-lo em lead ou paciente depois.
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                    <button
                      onClick={() => {
                        setShowNewContactModal(false);
                        setNewContactName('');
                        setNewContactPhone('');
                        setNewContactError(null);
                      }}
                      disabled={actionLoading === -1}
                      className={`flex-1 px-4 py-3 rounded-xl border font-medium transition-colors ${isDark
                        ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleCreateNewContact}
                      disabled={actionLoading === -1 || !newContactName.trim() || !newContactPhone.trim()}
                      className="flex-1 px-4 py-3 rounded-xl bg-sminy-primary text-white font-medium hover:bg-sminy-primary/90 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === -1 ? 'Verificando WhatsApp…' : 'Criar Contato'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default ContactsList;
